import { router } from 'expo-router';
import * as React from 'react';
import { Alert, Linking } from 'react-native';

import type { EventQuickActionsProps } from './event-quick-actions.types';
import { eventQuickActionIds, type EventQuickActionId } from '@/lib/event-quick-actions';
import { mapsUrl } from '@/lib/event-location';
import { formatEventWhen } from '@/lib/events';
import {
  addEventToAppleCalendar,
  downloadIcsCalendarEvent,
} from '@/lib/integrations/calendar-actions';
import { shareEventDetails } from '@/lib/share';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

const actionErrorTitles: Record<EventQuickActionId, string> = {
  calendar: 'Could not add to calendar',
  directions: 'Could not open directions',
  edit: 'Could not open event editor',
  share: 'Could not share event',
};

export function useEventQuickActions({
  clubName,
  clubSlug,
  editHref,
  event,
  timeZone,
}: EventQuickActionsProps) {
  const calendarExportEnabled = useReleaseFeatureFlag('calendarExport') === true;
  const actionIds = eventQuickActionIds({
    editAvailable: Boolean(editHref),
    status: event.status,
    tenantSlug: clubSlug,
  }).filter((actionId) => actionId !== 'calendar' || calendarExportEnabled);

  const runAction = React.useCallback(
    async (actionId: EventQuickActionId) => {
      try {
        if (actionId === 'edit') {
          if (editHref) {
            router.push(editHref);
          }
          return;
        }

        if (actionId === 'share') {
          if (!clubSlug) {
            return;
          }
          await shareEventDetails({
            eventId: event.id,
            eventTitle: event.title,
            eventWhen: formatEventWhen(event.starts_at, event.ends_at, timeZone),
            placeName: event.place_name,
            tenantName: clubName,
            tenantSlug: clubSlug,
          });
          return;
        }

        if (actionId === 'directions') {
          await Linking.openURL(mapsUrl(event));
          return;
        }

        if (process.env.EXPO_OS === 'web') {
          downloadIcsCalendarEvent(event);
        } else {
          await addEventToAppleCalendar(event, timeZone);
        }
      } catch (error) {
        Alert.alert(
          actionErrorTitles[actionId],
          error instanceof Error ? error.message : 'Please try again.'
        );
      }
    },
    [clubName, clubSlug, editHref, event, timeZone]
  );

  return { actionIds, runAction };
}
